/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 16:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';

import React, { Component } from 'react';
import { Card, TextStyle, Heading, Badge, Button } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { routes } from 'src/routes';
import history from 'src/routes/history';

import { trackClick } from 'src/helpers/mixpanel-helper';
import { getCorrectPlan } from 'src/helpers/subscription-v3-helper';
import { allowToShowBFCM23 } from 'src/helpers/dashboard-helper';

import * as subscriptionV3Actions from 'src/stores/actions/subscription-v3-actions';
import StarsIcon from 'src/assets/images/common/stars-2x.svg';
import SaleIcon from 'src/assets/images/common/sale-2x.png';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import ImgBgSubscription from 'src/assets/images/subscription-v3/subscription-bg.png';

type Props = {
  tenantApp: Object,
  subscription: Object
};

type ComposedProps = Props & WithI18nProps;

const StatusMapping = {
  // trial
  trial: 'trial',
  expiredSoon: 'trial',
  regularExpiredSoon: 'trial',
  // subscribed
  subscribed: 'subscribed',
  specialDiscountExpired: 'subscribed',
  oldSpecialDiscountExpired: 'subscribed',
  specialDiscountExpiredSoon: 'subscribed',
  specialDiscount2021ExpiredSoon: 'subscribed',
  // exceeded
  exceeded: 'exceeded',
  exceededWithNotification: 'subscribed'
};

class SubscriptionStatus extends Component<ComposedProps> {
  componentDidMount() {
    const { invoice = {}, getDetailInvoice, packagePlans, getPackagePlans } = this.props;
    // Get detail invoice
    if (_.isEmpty(invoice) || !invoice) {
      getDetailInvoice();
    }

    // Get package list
    if (!_.get(packagePlans, 'basic', []).length) {
      getPackagePlans();
    }
  }

  // Render content status
  renderContentStatus({ isBFCM23 } = {}) {
    let { subscription = {}, i18n, correctPlan = {} } = this.props;

    // Get subscription status
    const { status, dayLeft = 0 } = subscription;
    const { products = 1000 } = correctPlan;
    const originStatus = StatusMapping[status];

    const contentStatus = {
      trial: (
        <React.Fragment>
          <div className="subscribe__bottom">
            <p>
              {i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.trial_Paragraph', {
                nt_duration_in_day: (
                  <TextStyle variation="strong">
                    {i18n.translate(`AdminHTML.Common.Duration.day`, {
                      count: dayLeft,
                      nt_number: i18n.formatNumber(dayLeft)
                    })}
                  </TextStyle>
                )
              })}
            </p>
            <div className="subscribe__action">
              {isBFCM23 ? (
                <Button
                  primary
                  onClick={() => {
                    mixpanelV3Helper.trackEventData('Click on subscribe button', {
                      path: window.location.pathname,
                      buttonText: i18n.translate(
                        'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text_subscribe'
                      )
                    });
                    this.goToSubscriptionPage();
                  }}>
                  <img src={StarsIcon} alt="stars" style={{ marginRight: '4px' }} />
                  {i18n.translate(
                    'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text_subscribe'
                  )}
                </Button>
              ) : (
                <Button primary onClick={() => this.goToSubscriptionPage()}>
                  {i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.buttonText1')}
                </Button>
              )}
              <p className="subscribe-money-back">
                {i18n.translate('AdminHTML.Common.Subscription.moneyBackGuarantee')}
              </p>
            </div>
          </div>
        </React.Fragment>
      ),

      subscribed: (
        <React.Fragment>
          <div className="subscribe__bottom">
            <p className="success">
              {i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.subscribed_Paragraph', {
                nt_number: products
              })}
            </p>
            <div className="subscribe__action">
              {isBFCM23 ? (
                <Button
                  primary
                  onClick={() => {
                    mixpanelV3Helper.trackEventData('Click on subscribe button', {
                      path: window.location.pathname,
                      buttonText: i18n.translate(
                        'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text'
                      )
                    });
                    this.goToSubscriptionPage();
                  }}>
                  <img src={StarsIcon} alt="stars" style={{ marginRight: '4px' }} />
                  {i18n.translate(
                    'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text'
                  )}
                </Button>
              ) : (
                <Button onClick={() => this.goToSubscriptionPage()}>
                  {i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.buttonText2')}
                </Button>
              )}
              <p className="subscribe-money-back">
                {i18n.translate('AdminHTML.Common.Subscription.moneyBackGuarantee')}
              </p>
            </div>
          </div>
        </React.Fragment>
      ),

      exceeded: (
        <React.Fragment>
          <div className="subscribe__bottom">
            <p>{i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.exceeded_Paragraph')}</p>
            <div className="subscribe__action">
              {isBFCM23 ? (
                <Button
                  primary
                  onClick={() => {
                    mixpanelV3Helper.trackEventData('Click on subscribe button', {
                      path: window.location.pathname,
                      buttonText: i18n.translate(
                        'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text'
                      )
                    });
                    this.goToSubscriptionPage();
                  }}>
                  <img src={StarsIcon} alt="stars" style={{ marginRight: '4px' }} />
                  {i18n.translate(
                    'AdminHTML.Dashboard.SubscriptionStatus.BFCM2023_Discount_Percent_text'
                  )}
                </Button>
              ) : (
                <Button primary onClick={() => this.goToSubscriptionPage()}>
                  <span className="upgrade">
                    {i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.buttonText3')}
                  </span>
                </Button>
              )}
              <p className="subscribe-money-back">
                {i18n.translate('AdminHTML.Common.Subscription.moneyBackGuarantee')}
              </p>
            </div>
          </div>
        </React.Fragment>
      )
    };

    return contentStatus[originStatus];
  }

  render() {
    const { i18n, correctPlan = {}, tenantApp } = this.props;

    const { name: planName = 'basic' } = correctPlan;
    const packageName = planName.split('_')[0];
    const bfcm23 = allowToShowBFCM23(tenantApp);

    if (bfcm23.allow && bfcm23.type !== 'professional365') {
      return (
        <div className={`BC-Subscription__DashboardV3 BC-Subscription__DashboardV3--BFCM23`}>
          <img className="t-sale" src={SaleIcon} alt="sale" />
          <div className="t-semi t-left"></div>
          <div className="t-semi t-right"></div>
          <div className="t-line"></div>
          <Card sectioned>
            <Heading>{i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.title')}</Heading>
            <div className="current-package">
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.DashboardV3.SubscriptionStatus.packages.${packageName}`)}
              </TextStyle>
              {this.renderStatusBadge()}
            </div>
            {this.renderContentStatus({ isBFCM23: true })}
          </Card>
        </div>
      );
    }

    return (
      <div className="BC-Subscription__DashboardV3">
        <Card sectioned>
          <div className="wrapper">
            <Heading>{i18n.translate('AdminHTML.DashboardV3.SubscriptionStatus.title')}</Heading>
            <div className="current-package">
              <TextStyle variation="strong">
                {i18n.translate(`AdminHTML.DashboardV3.SubscriptionStatus.packages.${packageName}`)}
              </TextStyle>
              {this.renderStatusBadge()}
            </div>
            {this.renderContentStatus()}
          </div>
          <div className="img">
            <img
              className="subscription-bg"
              src={ImgBgSubscription}
              width={218}
              height={218}
              alt="back ground plan"
            />
          </div>
        </Card>
      </div>
    );
  }

  // Go to subscription page
  goToSubscriptionPage = isAllowSpecialDiscount => {
    history.push(routes.subscription.plan());
    trackClick('Select plan');
  };

  renderStatusBadge = () => {
    let { subscription = {}, i18n } = this.props;

    // Get subscription status
    let { status } = subscription;
    const originStatus = StatusMapping[status];

    const contentBadge = () => {
      if (originStatus === 'trial') {
        return {
          content: 'trial',
          status: 'warning'
        };
      }
      if (originStatus === 'exceeded') {
        return {
          content: 'exceeded',
          status: 'new'
        };
      }
      return {
        content: 'subscribed',
        status: 'success'
      };
    };

    const info = contentBadge();
    return (
      <Badge status={info.status}>
        {i18n.translate(`AdminHTML.DashboardV3.SubscriptionStatus.status.${info.content}`)}
      </Badge>
    );
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  const packagePlans = _.get(state.subscriptionV3, 'packagePlans', {});
  const chargeInfo = _.get(state.subscriptionV3, 'invoice.chargeInfo', {});
  const currentTotalProducts = _.get(tenantApp, 'currentTotalProducts');
  // Get correct plan
  const correctPlan = getCorrectPlan(chargeInfo, packagePlans, currentTotalProducts);

  return {
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    packagePlans,
    tenantApp,
    subscription: _.get(state.oauth, 'subscription', {}),
    correctPlan
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getPackagePlans: () => dispatch(subscriptionV3Actions.getPackagePlans(ownProps.i18n)),
  getDetailInvoice: () => dispatch(subscriptionV3Actions.getDetailInvoice(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SubscriptionStatus));
